import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";

const NAV_LINKS = [
  { label: "Benefits", href: "#benefits" },
  { label: "Product", href: "#product" },
  { label: "Why Ahaario", href: "#why" },
  { label: "Contact", href: "#contact" },
];

const AMAZON_URL = "https://amzn.in/d/02dgPPzh";

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-white/80 backdrop-blur-xl border-b border-[#E8EDE6]"
            : "bg-transparent"
        }`}
        data-testid="site-navigation"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 h-20 flex items-center justify-between">
          <a
            href="#top"
            className="flex items-center gap-2 group"
            data-testid="nav-brand-logo"
          >
            <div className="h-9 w-9 rounded-full bg-[#3E7D2B] flex items-center justify-center text-white font-display font-black text-lg shadow-md">
              A
            </div>
            <span className="font-display font-extrabold text-[#13240D] text-2xl tracking-tight">
              Ahaario
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-semibold text-[#4A5D44] hover:text-[#3E7D2B] transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-[#3E7D2B] hover:after:w-full after:transition-all after:duration-300"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={AMAZON_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-shop-now-btn"
              className="hidden sm:inline-flex items-center gap-2 bg-[#3E7D2B] hover:bg-[#2D5C1F] text-white rounded-full px-5 py-2.5 text-sm font-bold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg shadow-[#3E7D2B]/20"
            >
              <ShoppingBag className="h-4 w-4" />
              Shop Now
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden h-10 w-10 rounded-full border border-[#E8EDE6] flex items-center justify-center text-[#13240D]"
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="fixed top-20 left-0 right-0 z-40 lg:hidden bg-white/95 backdrop-blur-xl border-b border-[#E8EDE6] shadow-lg"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="text-base font-semibold text-[#13240D] py-2 border-b border-[#E8EDE6]"
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </a>
              ))}
              <a
                href={AMAZON_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#3E7D2B] text-white rounded-full px-5 py-3 font-bold"
                data-testid="mobile-shop-now-btn"
              >
                <ShoppingBag className="h-4 w-4" /> Shop on Amazon
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
